import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLazyGetReportSpecificFieldsQuery } from "../../../api/network/reportApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { TReport, TReportStatus } from "../../../api/types/Report";
import svgIcons from "../../../assets/svg/svgIcons";
import AccordianWithOptions from "../../../components/accordianWithOptions";
import AppSearchBox from "../../../components/searchBox";
import {
  APP_CONFIG,
  REPORT_CATEGORIES,
  REPORT_TYPES,
} from "../../../constants/constants";
import ModalReportOptions from "../selectAReport/modalReportOptions";
import { routeUrls } from "../../../navigation/routeUrls";
import ModalShareReportInternally from "../modalShareReportInternally";
import { useReportUtils } from "../hooks/reportUtils";

const ReportTypeItemButton = ({
  customClass = "",
  labelText,
  onClick,
  disabled = false,
  subLabelText,
  isSharedInternal = false,
  isSharedExternal = false,
  onSharedInternal,
  onSharedExternal,
  isExternallyScheduled,
}: {
  customClass?: string;
  labelText: ReactNode | string;
  subLabelText?: ReactNode | string;
  onClick: () => void;
  disabled?: boolean;
  isSharedInternal?: boolean;
  isSharedExternal?: boolean;
  isExternallyScheduled?: boolean;
  onSharedInternal?: () => void;
  onSharedExternal?: () => void;
}) => {
  const totalButtons =
    (isSharedInternal ? 1 : 0) +
    (isSharedExternal ? 1 : 0) +
    (isExternallyScheduled ? 1 : 0);
  const peClass =
    totalButtons > 2 ? "pe-32" : totalButtons > 1 ? "pe-[84px]" : "pe-10"; // 10 per button + 1 gap
  return (
    <div className="flex justify-between relative">
      <button
        className={`${disabled ? "opacity-50 " : ""}${
          customClass ||
          `flex-1 items-center flex justify-between gap-4 ps-6 ${peClass} py-3 text-left text-sm font-medium text-sidemenu-text hover:bg-slate-100`
        }`}
        onClick={onClick}
        disabled={disabled}
      >
        <span className={!!subLabelText ? `max-w-[60%]` : ""}>{labelText}</span>
        {subLabelText && (
          <span className="text-right italic text-xs text-gray-400">
            {subLabelText}
          </span>
        )}
      </button>
      <div className="flex gap-1 absolute right-0 top-1/2 transform -translate-y-1/2">
        {isSharedInternal && (
          <button
            className="size-10 flex items-center justify-center rounded-lg hover:bg-accent-blue-paleO66 focus:bg-accent-blue-paleO66"
            onClick={onSharedInternal}
          >
            <img src={svgIcons.UsersIcon} alt="Users" className="w-6" />
          </button>
        )}
        {isSharedExternal && (
          <button
            className="size-10 flex items-center justify-center rounded-lg hover:bg-accent-blue-paleO66 focus:bg-accent-blue-paleO66"
            onClick={onSharedExternal}
          >
            <img src={svgIcons.SharedExt} alt="Users" className="w-4" />
          </button>
        )}
        {isExternallyScheduled && (
          <button
            className="size-10 flex items-center justify-center rounded-lg hover:bg-accent-blue-paleO66 focus:bg-accent-blue-paleO66"
            onClick={onSharedExternal}
          >
            <img src={svgIcons.TimerIcon} alt="Timer" className="w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const noItemsClass = "grayscale opacity-30";
const popupMaxHeightClass = "mt-2 max-h-[calc(100vh-280px)] overflow-auto";

export const ReportTypeSelectorSearchResultsDropdown: FC<{
  wrapperClass?: string;
  searchKeyword: string;
}> = ({
  wrapperClass = "absolute top-full p-4 left-0 mt-1 w-full max-h-96 overflow-auto rounded-lg shadow-lg bg-white z-10",
  searchKeyword,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.selectAType",
  });
  const reportsByType: { [key: string]: TReport[] } = useSelector(
    (state: any) => state.commonReducer.reportsByType
  );

  const totalReportsCountWhenUsedAsSearchResults =
    reportsByType[REPORT_TYPES.STANDARD].filter((report) =>
      !searchKeyword
        ? true
        : report.name?.toLowerCase().includes(searchKeyword.toLowerCase())
    ).length +
    reportsByType[REPORT_TYPES.CUSTOM]?.filter((report) =>
      !searchKeyword
        ? true
        : report.name?.toLowerCase().includes(searchKeyword.toLowerCase())
    ).length +
    reportsByType[REPORT_TYPES.SHARED_WITH_ME]?.filter((report) =>
      !searchKeyword
        ? true
        : report.name?.toLowerCase().includes(searchKeyword.toLowerCase())
    ).length; //+
  // reportsByType[REPORT_TYPES.SHARED_WITH_OTHERS]
  //   ?.filter(report => !searchKeyword ? true : report.name?.toLowerCase().includes(searchKeyword.toLowerCase()))
  //   .length;

  return (
    <>
      <div className={wrapperClass}>
        <ReportTypeSelector
          singleColumn={true}
          usedAsSearchResults={searchKeyword}
        />
        {totalReportsCountWhenUsedAsSearchResults === 0 && (
          <p className="px-6 py-3 text-left text-sm text-gray-400">
            {t("no_items")}
          </p>
        )}
      </div>
    </>
  );
};

type TReportTypeSelectorProps = {
  showOnlyResultsOnSearch?: boolean;
  singleColumn?: boolean;
  usedAsSearchResults?: string | false;
};

const ReportTypeSelector: FC<TReportTypeSelectorProps> = ({
  showOnlyResultsOnSearch = false,
  singleColumn = false,
  usedAsSearchResults = false,
}) => {
  const { t: tReports } = useTranslation("translation", {
    keyPrefix: "reports",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.selectAType",
  });
  const navigate = useNavigate();

  const { navigateOnReportOptionsChange, navigateToReportPage } =
    useReportUtils();

  const reportsByType: { [key: string]: TReport[] | TReportStatus } =
    useSelector((state: any) => state.commonReducer.reportsByType);
  const dispatch = useDispatch();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const col2ReportList = [REPORT_TYPES.CUSTOM];
  const col3ReportList = [REPORT_TYPES.SHARED_WITH_ME]; // ,REPORT_TYPES.SHARED_WITH_OTHERS];

  const [openReportCol, setOpenReportCol] = useState<(string | null)[]>([
    null,
    col2ReportList[0],
    col3ReportList[0],
  ]);
  // const [openReportCol, setOpenReportCol] = useState<(string | null)[]>(Array(reportColSize).fill(null));

  const [searchKeyword, setSearchKeyword] = useState<string>(
    usedAsSearchResults || ""
  );
  useEffect(() => {
    if (usedAsSearchResults) {
      setSearchKeyword(usedAsSearchResults);
    }
  }, [usedAsSearchResults]);

  const [
    getSpecificReportQuery /*, { isFetching: isFetchingSpecificReportQuery }*/,
  ] = useLazyGetReportSpecificFieldsQuery();

  const BlankStandardAccordion = useCallback(() => {
    return (
      <AccordianWithOptions
        wrapperClassName={noItemsClass}
        title={tReports(`titles.type_1`)}
        isOpen={true}
        setIsOpen={() => {}}
        collapsible={true}
      >
        <div className="flex flex-col pt-3 pb-6">
          <p className="px-6 py-3 text-left text-sm text-gray-400">
            {t("no_items")}
          </p>
        </div>
      </AccordianWithOptions>
    );
  }, [t, tReports]);

  const ReportTypeAccordion: React.FC<{
    reportType: string;
    categorized: boolean;
  }> = ({ reportType, categorized = false }) => {
    // useCallback(
    let firstVisibleIndex: number | null = null;

    if (!!!reportsByType[reportType as REPORT_TYPES])
      return (
        <div className={singleColumn ? "" : "lg:col-span-4"}>
          <BlankStandardAccordion />
        </div>
      );

    let visibleStandardCategories = 0;
    return (
      <div className={singleColumn ? "" : "lg:col-span-4"}>
        {Object.keys(REPORT_CATEGORIES)
          // alphabetical order
          .sort((a, b) => {
            const a_translated = tReports(
              `titles.report_category_${
                REPORT_CATEGORIES[a as keyof typeof REPORT_CATEGORIES]
              }`
            );
            const b_translated = tReports(
              `titles.report_category_${
                REPORT_CATEGORIES[b as keyof typeof REPORT_CATEGORIES]
              }`
            );
            return a_translated.localeCompare(b_translated);
          })
          // render
          .map((reportCategoryKey, index) => {
            const filteredReports =
              typeof (reportsByType[reportType] as TReportStatus)?.loading !==
              "undefined"
                ? []
                : (reportsByType[reportType] as TReport[])
                    ?.filter(
                      (report) =>
                        report.category ===
                        parseInt(
                          REPORT_CATEGORIES[
                            reportCategoryKey as keyof typeof REPORT_CATEGORIES
                          ]
                        )
                    )
                    ?.filter((report) =>
                      !usedAsSearchResults || !searchKeyword
                        ? true
                        : report.name
                            ?.toLowerCase()
                            .includes(searchKeyword.toLowerCase())
                    ) || [];
            if (filteredReports.length === 0) {
              return null;
            }
            visibleStandardCategories++;
            if (firstVisibleIndex === null) firstVisibleIndex = index;
            return (
              <AccordianWithOptions
                key={index}
                title={tReports(
                  `titles.report_category_${
                    REPORT_CATEGORIES[
                      reportCategoryKey as keyof typeof REPORT_CATEGORIES
                    ]
                  }`
                )}
                isOpen={
                  /*!!searchKeyword
                    ? index === firstVisibleIndex
                    : */
                  usedAsSearchResults
                    ? true
                    : openReportCol[0] === reportCategoryKey
                }
                setIsOpen={() => {
                  // setOpenReportCol1(openReportCol1 === reportCategoryKey ? null : reportCategoryKey);
                  setOpenReportCol([
                    openReportCol[0] === reportCategoryKey
                      ? null
                      : reportCategoryKey,
                    openReportCol[1],
                    openReportCol[2],
                  ]);
                }}
                collapsible={true}
                showButton={!usedAsSearchResults}
                isLoading={
                  !!(reportsByType[reportType] as TReportStatus)?.loading
                }
              >
                <div
                  className={`flex flex-col${
                    usedAsSearchResults ? "" : " pt-3 pb-6"
                  }`}
                >
                  {filteredReports.map((subReport, subIndex) => {
                    return (
                      <ReportTypeItemButton
                        key={subIndex}
                        customClass={
                          usedAsSearchResults
                            ? "flex-grow px-4 py-3 text-left text-sm font-semibold text-sidemenu-text rounded-lg hover:bg-accent-blue-paleO66"
                            : undefined
                        }
                        labelText={
                          usedAsSearchResults ? (
                            <>
                              <span className="text-field-label-valid">
                                {tReports(
                                  `titles.report_category_${
                                    REPORT_CATEGORIES[
                                      reportCategoryKey as keyof typeof REPORT_CATEGORIES
                                    ]
                                  }`
                                )}{" "}
                                &rsaquo;{" "}
                              </span>
                              <span className="text-sidemenu-submenu-text-active">
                                {subReport.name}
                              </span>
                            </>
                          ) : (
                            subReport.name
                          )
                        }
                        onClick={() => {
                          setSelectedReport({
                            reportType: REPORT_TYPES.STANDARD,
                            reportId: subReport.id,
                          });
                          dispatch(
                            setModalsData({
                              ...modalsState,
                              showReportOptions: true,
                            })
                          );
                        }}
                      />
                    );
                  })}
                </div>
              </AccordianWithOptions>
            );
          })}
        {visibleStandardCategories === 0 && !usedAsSearchResults && (
          <BlankStandardAccordion />
        )}
      </div>
    );
  };
  //, [openReportCol1, reportsByType, searchKeyword, tReports]);

  const [selectedReport, setSelectedReport] = useState<{
    reportType: string; // keyof typeof REPORT_TYPES;
    reportId: number;
  } | null>(null);

  const handleOnSharedExternal = ({ type, category, id }: TReport) => {
    const newUrl =
      routeUrls.dashboardChildren.reportsChildren.reportPrefix +
      routeUrls.dashboardChildren.reportsChildren.reportSharingSuffixUri
        .replace(":reportType", `${type}`)
        .replace(":categoryId", `${category}`)
        .replace(":reportId", `${id}`);
    navigate(newUrl);
  };

  const [selectedInternalReport, setSelectedInternalReport] = useState<
    number | null
  >(null);

  const handleOnSharedInternal = ({ id }: TReport) => {
    dispatch(
      setModalsData({
        ...modalsState,
        showInternalShareReportModal: true,
      })
    );
    setSelectedInternalReport(id);
  };

  return (
    <>
      {!usedAsSearchResults && (
        <>
          {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && !singleColumn && (
            <pre className="col-span-12 absolute text-xs pointer-events-none opacity-30">
              {JSON.stringify(reportsByType, null, 2)}
            </pre>
          )}
          {/* Search box */}
          <div className="lg:grid lg:grid-cols-12 py-2 gap-4">
            <div
              className={`${
                singleColumn ? "lg:col-span-12" : "lg:col-span-6"
              } relative${!!searchKeyword ? " z-below-popup-overlay" : ""}`}
            >
              <AppSearchBox
                placeholder={t("search_placeholder")}
                initialValue={searchKeyword}
                onChange={(event) => {
                  setSearchKeyword(event.target.value);
                }}
                onClear={() => {
                  setSearchKeyword("");
                  setOpenReportCol([
                    Object.keys(REPORT_CATEGORIES)[0],
                    col2ReportList[0],
                    col3ReportList[0],
                  ]);
                }}
                disabled={
                  !Array.isArray(reportsByType?.[REPORT_TYPES.NEW_CUSTOM])
                }
              />
              {/* using same component `ReportTypeSelector` for search results list */}
              {!!searchKeyword && (
                <ReportTypeSelectorSearchResultsDropdown
                  searchKeyword={searchKeyword}
                  wrapperClass={
                    showOnlyResultsOnSearch
                      ? `relative ${popupMaxHeightClass}`
                      : "absolute top-full p-4 left-0 mt-1 w-full max-h-96 overflow-auto rounded-lg shadow-lg bg-white z-10"
                  }
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* List of reports */}
      {((showOnlyResultsOnSearch && !searchKeyword) || // if showOnlyResultsOnSearch, then show only when searchKeyword is empty
        !showOnlyResultsOnSearch) && ( // if not showOnlyResultsOnSearch, then show always
        <div
          className={`${
            singleColumn
              ? `${showOnlyResultsOnSearch ? `${popupMaxHeightClass} ` : ""}`
              : "lg:grid lg:grid-cols-12 pb-8 "
          }pt-2 gap-4`}
        >
          {/* Standard Reports */}
          {/* {reportsByType[REPORT_TYPES.STANDARD] && ( */}
          <ReportTypeAccordion
            reportType={REPORT_TYPES.STANDARD}
            categorized={true}
          />
          {/* )} */}

          {/* TODO: Create search-enabled memoized component for Col 2 and 3 */}
          {[col2ReportList, col3ReportList].map((colReportList, colIndex) => (
            <div className={singleColumn ? "" : "lg:col-span-4"} key={colIndex}>
              {colReportList.map((reportTypeId, index) => {
                const filteredReports =
                  typeof (reportsByType[reportTypeId] as TReportStatus)
                    ?.loading !== "undefined"
                    ? []
                    : (reportsByType[reportTypeId] as TReport[])?.filter(
                        (report) =>
                          !usedAsSearchResults || !searchKeyword
                            ? true
                            : report.name
                                ?.toLowerCase()
                                .includes(searchKeyword.toLowerCase())
                      );
                return (
                  <AccordianWithOptions
                    wrapperClassName={
                      typeof (reportsByType[reportTypeId] as TReportStatus)
                        ?.loading !== "undefined" || filteredReports?.length > 0
                        ? ""
                        : noItemsClass
                    }
                    key={index}
                    title={tReports(`titles.type_${reportTypeId}`)}
                    isOpen={openReportCol[colIndex + 1] === reportTypeId}
                    setIsOpen={() => {
                      // setOpenReportCol2(openReportCol2 === reportTypeId ? null : reportTypeId);
                      const newOpenReportCol = [...openReportCol];
                      newOpenReportCol[colIndex + 1] =
                        openReportCol[colIndex + 1] === reportTypeId
                          ? null
                          : reportTypeId;
                      setOpenReportCol(newOpenReportCol);
                    }}
                    collapsible={true}
                    showButton={!usedAsSearchResults}
                    isLoading={
                      !!(reportsByType[reportTypeId] as TReportStatus)?.loading
                    }
                  >
                    <div
                      className={`flex flex-col${
                        usedAsSearchResults ? "" : " pt-3 pb-6"
                      }`}
                    >
                      {!!filteredReports && filteredReports.length > 0 ? (
                        <>
                          {filteredReports?.map((subReport, subIndex) => {
                            return (
                              <ReportTypeItemButton
                                key={subIndex}
                                customClass={
                                  usedAsSearchResults
                                    ? "flex-grow pl-4 pr-4 py-3 text-left text-sm font-semibold text-sidemenu-text rounded-lg hover:bg-accent-blue-paleO66"
                                    : undefined
                                }
                                labelText={
                                  usedAsSearchResults ? (
                                    <>
                                      <span className="text-field-label-valid">
                                        {tReports(
                                          `titles.type_${reportTypeId}`
                                        )}{" "}
                                        &rsaquo;{" "}
                                      </span>
                                      <span className="text-sidemenu-submenu-text-active">
                                        {subReport.name}
                                      </span>
                                    </>
                                  ) : (
                                    subReport.name
                                  )
                                }
                                subLabelText={
                                  reportTypeId ===
                                    REPORT_TYPES.SHARED_WITH_ME &&
                                  subReport.shared_by
                                }
                                isSharedInternal={
                                  subReport.is_shared_internally &&
                                  reportTypeId === REPORT_TYPES.CUSTOM
                                    ? true
                                    : undefined
                                }
                                isSharedExternal={
                                  subReport.is_shared_externally &&
                                  reportTypeId === REPORT_TYPES.CUSTOM
                                    ? true
                                    : undefined
                                }
                                isExternallyScheduled={
                                  subReport.is_externally_scheduled &&
                                  reportTypeId === REPORT_TYPES.CUSTOM
                                    ? true
                                    : undefined
                                }
                                onClick={() => {
                                  navigateToReportPage(
                                    subReport.id,
                                    reportTypeId
                                  );
                                  // converted the code below to navigateToReportPage
                                  // getSpecificReportQuery({
                                  //   report_id: subReport.id,
                                  //   type: reportTypeId,
                                  // })
                                  //   .unwrap()
                                  //   .then((response) => {
                                  //     const reportFilters =
                                  //       response?.report_filters?.reduce(
                                  //         (acc, filter) => {
                                  //           acc[filter.param] = filter.value;
                                  //           return acc;
                                  //         },
                                  //         {} as { [key: string]: string }
                                  //       );

                                  //     // navigate to generate report
                                  //     // handleReportOptionsSubmit(reportFilters, REPORT_TYPES.CUSTOM, subReport.category, subReport.id, navigate);
                                  //     navigateOnReportOptionsChange({
                                  //       filterValues: reportFilters,
                                  //       reportType: reportTypeId,
                                  //       category: subReport.category,
                                  //       reportId: subReport.id,
                                  //       navigate,
                                  //       reportFilters: response?.report_filters,
                                  //     });
                                  //     dispatch(
                                  //       setModalsData({
                                  //         ...modalsState,
                                  //         showReportTypeSelector: false,
                                  //       })
                                  //     );
                                  //   })
                                  //   .catch((error) => {
                                  //     console.log("error", error);
                                  //     toast.error(
                                  //       t("toast.get_specific_report_error")
                                  //     );
                                  //   });
                                }}
                                onSharedInternal={() => {
                                  handleOnSharedInternal(subReport);
                                }}
                                onSharedExternal={() => {
                                  handleOnSharedExternal(subReport);
                                }}
                              />
                            );
                          })}
                        </>
                      ) : !usedAsSearchResults ? (
                        <p className="px-6 py-3 text-left text-sm text-gray-400">
                          {t("no_items")}
                        </p>
                      ) : null}
                    </div>
                  </AccordianWithOptions>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {!!selectedReport && (
        <ModalReportOptions
          reportType={selectedReport?.reportType}
          reportId={selectedReport?.reportId}
          onSubmit={({
            filterValues,
            reportType,
            category,
            reportId,
            navigate,
            reportFilters,
          }) => {
            dispatch(
              setModalsData({ ...modalsState, showReportTypeSelector: false })
            );

            // navigate to generate report
            navigateOnReportOptionsChange({
              filterValues,
              reportType,
              category,
              reportId,
              navigate,
              reportFilters,
            });

            // reset selected report
            setSelectedReport(null);
          }}
          onClose={() => setSelectedReport(null)}
        />
      )}
      {modalsState.showInternalShareReportModal && selectedInternalReport && (
        <ModalShareReportInternally reportId={selectedInternalReport} />
      )}
    </>
  );
};
export default ReportTypeSelector;
